// eslint-disable-next-line no-restricted-imports
import ClipboardBunny from "@src/assets/images/clipboard-bunny.png";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import QRCodeStyling, { type Options } from "qr-code-styling";
import { useMemo } from "react";

export const DEFAULT_QR_CODE_OPTIONS: Options = {
  width: 600,
  height: 600,
};

export const BRANDED_QR_CODE_OPTIONS: Options = {
  ...DEFAULT_QR_CODE_OPTIONS,
  image: ClipboardBunny,
  imageOptions: {
    imageSize: 0.4,
    margin: 24,
  },
  qrOptions: {
    errorCorrectionLevel: "H",
  },
  backgroundOptions: {
    color: "#f3f3f3",
    round: 12,
  },
};

export const LEGACY_BRANDED_QR_CODE_OPTIONS: Options = {
  ...DEFAULT_QR_CODE_OPTIONS,
  image: ClipboardBunny,
  imageOptions: {
    imageSize: 0.4,
    margin: 24,
  },
  qrOptions: {
    errorCorrectionLevel: "H",
  },
};

export function useStyledQrCodeDataUrl(
  params: { data: string; qrOptions?: Options },
  options: UseQueryOptions<string> = {}
): UseQueryResult<string> {
  const { data, qrOptions = DEFAULT_QR_CODE_OPTIONS } = params;
  const jsonQrOptions = JSON.stringify(qrOptions);

  // Memoize the QRCodeStyling instance so it isn't recreated on every render
  // Use JSONified options for dependency array due to object reference issues
  const qrCode = useMemo(() => {
    const qrCodeOptions = JSON.parse(jsonQrOptions) as Options;
    return new QRCodeStyling({ data, ...qrCodeOptions });
  }, [data, jsonQrOptions]);

  return useQuery({
    queryKey: ["qrCodeDataUrl", data, qrOptions],
    queryFn: async () => {
      const result = await qrCode.getRawData();
      if (!result) {
        throw new Error("Failed to generate QR code");
      }

      // Handle Blob or Buffer result
      // Create new Uint8Array to ensure proper ArrayBuffer type for TypeScript
      const blob = result instanceof Blob ? result : new Blob([new Uint8Array(result).buffer]);

      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === "string") {
            resolve(reader.result);
          } else {
            reject(new Error("Could not convert QR Blob to data URL"));
          }
        };

        reader.addEventListener("error", () => {
          reject(reader.error);
        });

        reader.readAsDataURL(blob);
      });
    },
    ...options,
  });
}
